// src/hooks/useUserPresence.ts
import { useEffect, useRef } from "react";
import { workersClient } from "@/integrations/database/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Hook to track user presence (online/offline) status
 * OPTIMIZED: Sends heartbeat every 2 minutes (was 30 seconds)
 * Only updates when tab is visible to reduce API calls
 */

// OPTIMIZATION: Increased from 30s to 2 minutes to reduce egress traffic by 75%
const PRESENCE_UPDATE_INTERVAL = 120000; // 120 seconds (2 minutes)

export const useUserPresence = () => {
  const { user } = useAuth();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isDocumentVisible = useRef<boolean>(true);

  useEffect(() => {
    if (!user?.id) {
      // Clear interval if user logs out
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Function to update presence
    const updatePresence = async () => {
      // OPTIMIZATION: Only update if tab is visible
      if (!isDocumentVisible.current) {
        console.log("[useUserPresence] Tab inactive, skipping update");
        return;
      }

      try {
        // Direct UPDATE instead of RPC (TypeScript types not yet generated)
        const { error } = await workersClient
          .from("users")
          .update({ last_seen: new Date().toISOString() })
          .eq("id", user.id);

        if (error) {
          console.error("[useUserPresence] Error updating presence:", error);
        } else {
          console.log("[useUserPresence] Presence updated successfully");
        }
      } catch (err) {
        console.error("[useUserPresence] Exception updating presence:", err);
      }
    };

    // OPTIMIZATION: Track document visibility to pause updates when tab is hidden
    const handleVisibilityChange = () => {
      isDocumentVisible.current = !document.hidden;

      // Immediately update presence when tab becomes visible again
      if (isDocumentVisible.current) {
        console.log("[useUserPresence] Tab became visible, updating presence");
        updatePresence();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Initial presence update
    updatePresence();

    // OPTIMIZATION: Interval increased from 30s to 2 minutes
    // This reduces API calls by 75% (from 120 calls/hour to 30 calls/hour per user)
    intervalRef.current = setInterval(() => {
      updatePresence();
    }, PRESENCE_UPDATE_INTERVAL);

    // Cleanup on unmount or user change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [user?.id]);

  // REMOVED: Window focus listener (replaced by visibilitychange which is more reliable)
  // Previous implementation caused duplicate updates

  return null; // This hook doesn't return anything, just manages side effects
};
